var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/// <reference path="../node_modules/paper/dist/paper-core.d.ts" />
import paper from "paper/dist/paper-core";
import { Events } from "@auvious/utils";
import { EraserTool } from "./tools/eraser";
import { ArrowTool } from "./tools/arrow";
import { PathTool } from "./tools/path";
import { MESSAGES, Messenger } from "./network";
import { OPS, type2ops } from "./utils";
import { recover, ToolPostFailed } from "./errors";
let inStatic = false;
export class Sketch {
    constructor(canvas, client) {
        this.canvas = canvas;
        this.events = new Events();
        this._scaleValue = 1;
        this.autoScale = false;
        this.syncedWidth = 1280.0;
        // scrolling
        this.top = 0;
        this.left = 0;
        if (!inStatic) {
            throw new Error("Constructor called. Use Sketch.create instead.");
        }
        else {
            inStatic = false;
        }
        this.messenger = new Messenger(client, this);
        this.scope = new paper.PaperScope();
        this.scope.activate();
        this.scope.setup(this.canvas);
        this.noTool = new this.scope.Tool();
        this.marker = new PathTool(this);
        this.eraser = new EraserTool(this);
        this.arrow = new ArrowTool(this);
        this.marker.onDrawn = (ev) => this.markerDrawnCallback(ev);
        this.arrow.onDrawn = (ev) => this.arrowDrawnCallback(ev);
        this.eraser.onErased = (ev) => this.eraserCallback(ev);
        this.messenger.onMessage = (ev) => this.processIncomingMessage(ev);
        this.noTool.activate();
    }
    static create(canvas, auviousClient) {
        inStatic = true;
        return new Sketch(canvas, auviousClient);
    }
    /**
     * Make the sketch collaborative
     * @param options
     * @returns sketchId
     */
    connect(options) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.messenger.ready) {
                return;
            }
            this.userId = options.userId;
            this.userEndpointId = options.userEndpointId;
            this.scope.activate();
            this.scope.project.activeLayer.children.forEach((item) => {
                item.data.userId = this.userId;
                item.data.userEndpointId = this.userEndpointId;
            });
            this.messenger.setup();
            if (options.sketchId) {
                this.sketchId = yield this.messenger.join(options.sketchId);
            }
            else {
                this.sketchId = yield this.messenger.create();
            }
            return this.sketchId;
        });
    }
    markerDrawnCallback(ev) {
        recover(() => __awaiter(this, void 0, void 0, function* () {
            return this.messenger.post([
                {
                    op: OPS.MarkerCreated,
                    data: ev,
                },
            ]);
        }), () => this.events.emit("error", new ToolPostFailed("Failed to synchronize marker")));
    }
    arrowDrawnCallback(ev) {
        recover(() => __awaiter(this, void 0, void 0, function* () {
            return this.messenger.post([
                {
                    op: OPS.ArrowCreated,
                    data: ev,
                },
            ]);
        }), () => this.events.emit("error", new ToolPostFailed("Failed to synchronize arrow")));
    }
    eraserCallback(ev) {
        recover(() => __awaiter(this, void 0, void 0, function* () {
            return this.messenger.post([
                {
                    op: OPS.Erased,
                    data: ev,
                },
            ]);
        }), () => this.events.emit("error", new ToolPostFailed("Failed to synchronize removal")));
    }
    processIncomingMessage(msg) {
        switch (msg.type) {
            case MESSAGES.CanvasUpdated: {
                this.processSketchEvents(msg.payload.events);
                break;
            }
            case MESSAGES.UserJoined: {
                this.messenger.post(this.serializeChildren(), Object.assign({}, msg.payload));
                break;
            }
        }
    }
    serializeChildren() {
        this.scope.activate();
        return this.scope.project.activeLayer.children
            .filter((item) => item.data.userEndpointId === this.userEndpointId)
            .map((item) => ({
            op: type2ops[item.data.tool],
            data: item.exportJSON({ asString: false }),
        }));
    }
    processSketchEvents(events) {
        for (const event of events) {
            switch (event.op) {
                case OPS.MarkerCreated: {
                    this.marker.draw(event.data);
                    break;
                }
                case OPS.ArrowCreated: {
                    this.arrow.draw(event.data);
                    break;
                }
                case OPS.Erased: {
                    this.eraser.erase(event.data);
                    break;
                }
                case OPS.Cleared: {
                    this.clearLayer();
                    break;
                }
            }
        }
    }
    set scaleValue(value) {
        if (isFinite(value)) {
            this._scaleValue = value || 0.001;
        }
        else {
            this._scaleValue = 1000 * Math.sign(value);
        }
    }
    get scaleValue() {
        return this._scaleValue;
    }
    clearLayer() {
        this.scope.project.activeLayer.removeChildren();
    }
    clear() {
        this.clearLayer();
        this.messenger.post([{ op: OPS.Cleared }]);
    }
    scrollTo(top, left) {
        this.top = top;
        this.left = left;
        // @ts-ignore
        this.scope.view.scrollBy(left - this.scope.view.bounds.x, top - this.scope.view.bounds.y);
    }
    scale(scale, center) {
        this.scaleValue = scale / this.scaleValue;
        center
            ? this.scope.view.scale(this.scaleValue, new paper.Point(center))
            : this.scope.view.scale(this.scaleValue);
        this.scaleValue = scale;
    }
    mirror() {
        this.scope.view.scale(-1, 1);
    }
    resize(x, y) {
        this.scope.view.viewSize = new paper.Size(x || 1, y || 1);
        if (this.autoScale) {
            this.scale(x / this.syncedWidth, new paper.Point({ x: 0, y: 0 }));
        }
        // this fixes translation offset when mirrored
        // however the underlying reason for the disrepancy is unknown
        this.scrollTo(this.top, this.left);
    }
    lockRatio(state, width = 720) {
        this.syncedWidth = 1.0 * width;
        this.autoScale = state;
    }
    snapshot() {
        const canvas = document.createElement("canvas");
        canvas.width = this.canvas.width;
        canvas.height = this.canvas.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(this.canvas, 0, 0);
        return canvas;
    }
    serialize() {
        return JSON.stringify(this.serializeChildren());
    }
    deserialize(sketch) {
        this.processSketchEvents(JSON.parse(sketch));
    }
}
